import { Avatar } from "@radix-ui/themes";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { uploadUrl } from "@/config/publicConfig";
import { useFetch } from "../../hooks/useFetch";
import { useAppStore } from "../../stores/RootStore";
import { Person, Upload } from "../../types/types";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input/Input";
import { ScrollArea } from "../../ui/ScrollArea/ScrollArea";

const AddStaff = () => {
  const navigate = useNavigate()
  const {persons, setPersons} = useAppStore();
  const [imageId, setImageId] = useState("");
  const [image, setImage] = useState<object>();

  const [errors, setErrors] = useState([]);
  const { reset, handleSubmit, control } = useForm({
    defaultValues: {
      birth_date: undefined,
      first_name: "",
      image: "",
      last_name: "",
      middle_name: "",
      personnel_number: "",
    },
  });

  const [{ response: itemSaveResponse, isLoading: itemSaveIsLoading, error: itemSaveError }, doFetchItemSave] =
    useFetch(`/directories/personnels`);

  const [
    { response: imageUploadResponse, isLoading: imageUploadIsLoading, error: imageUploadError },
    doFetchImageUpload,
  ] = useFetch(`/uploads`, true, true);

  const onSubmit = (formData: any) => {
    doFetchItemSave({
      method: "POST",
      data: {
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          middle_name: formData.middle_name,
          birth_date: formData.birth_date,
          personnel_number: formData.personnel_number,
          image: image,
        },
      },
    });
    reset();
  };

  const onFileChoice = (value: string | Blob | File) => {
    const formData = new FormData();
    formData.set("attachment", value);
    doFetchImageUpload({
      method: "POST",
      data: formData,
    });
  };

  useEffect(() => {
    if (!imageUploadResponse) {
      return;
    }
    const imageUploadData = imageUploadResponse.data as unknown as Upload;
    setImageId(imageUploadData.id);
    setImage(imageUploadData);
  }, [imageUploadResponse]);

  const addPerson = (itemSaveResponse: { data: unknown }) => {
    const newPersonData = itemSaveResponse.data as unknown as Person;
    setPersons(
      [...persons!, newPersonData].sort((a, b) =>
        a.attributes.first_name.toLowerCase() > b.attributes.first_name.toLowerCase() ? 1 : -1 
      )
    );
  };

  useEffect(() => {
    if (!itemSaveResponse) return;
    addPerson(itemSaveResponse);
    reset();
    navigate("..", { relative: "path" });
  }, [itemSaveResponse]);

  return (
    <ScrollArea className="w-full h-full bg-background">
      <div className="flex flex-col max-w-[900px] w-full justify-start mx-auto pl-4 pr-16 relative overflow-hidden my-[4rem]">
        <div className="text-xl font-semibold">{t("adding_staff")}</div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col w-full pt-8 h-full gap-3">
            <div className="flex space-x-6">
              <Avatar size="7" src={imageId ? uploadUrl(imageId) : ""} fallback="A" />
              <div className="flex flex-col space-y-2 items-start">
                <div className="font-medium text-sm">{imageId ? t('change_staff_avatar') : t('upload_staff_avatar')}</div>
                <label className="flex flex-col items-center justify-center h-full">
                  <span className="text-base leading-normal bg-muted px-2 py-1 rounded cursor-pointer hover:bg-primary hover:text-white">{t('select_image')}</span>
                  {/*@ts-ignore */}
                  <input className="hidden" type="file" onChange={(evt) => onFileChoice(evt.target.files[0])} />
                </label>
                <div className="text-xs">{t('upload_avatar_description')}</div>
              </div>
            </div>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("name")} />
                </div>
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("surname")} />
                </div>
              )}
            />
            <Controller
              name="middle_name"
              control={control}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("middlename")} />
                </div>
              )}
            />
            <Controller
              name="personnel_number"
              control={control}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("personnel_number")} />
                </div>
              )}
            />
            <Button type="submit" className="mt-8">
              {t("save")}
            </Button>
          </div>
        </form>
      </div>
    </ScrollArea>
  );
};

export default AddStaff;
